import { Link } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { EtainablSpinner } from '@/components/etainabl-spinner';
import { Button } from '@/components/ui/button';
import type { KbArticle } from '@/types/kb';
import { KbRestrictedBadge } from './KbRestrictedBadge';

// The reader-facing article list, shared by search results and a collection's page.
export function KbArticleList({
  articles,
  isLoading,
  emptyText,
  isAdmin,
  showCollection,
  page
}: {
  articles?: KbArticle[];
  isLoading: boolean;
  emptyText: string;
  isAdmin: boolean;
  showCollection?: boolean;
  // The API caps a page at 100, so long lists page through with skip.
  page?: { total: number; skip: number; limit: number; onSkip: (skip: number) => void };
}) {
  if (isLoading) {
    return (
      <div className="py-8 flex justify-center">
        <EtainablSpinner size="sm" />
      </div>
    );
  }
  if (!articles?.length) return <p className="text-sm text-muted-foreground py-8 text-center">{emptyText}</p>;

  return (
    <ul className="divide-y rounded border bg-background">
      {articles.map(a => (
        <li key={a._id}>
          <Link to={`/kb/${a.slug}` as never} className="block px-4 py-3 hover:bg-muted transition-colors">
            <div className="font-medium">{a.title}</div>
            {a.subtitle && <div className="text-sm text-muted-foreground mt-0.5">{a.subtitle}</div>}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1">
              {showCollection && a.collection && <span data-testid="kb-article-collection">{a.collection.name}</span>}
              <span>Updated {dayjs(a.updatedAt).fromNow()}</span>
              {!a.isPublished && <span className="text-amber-600">Draft</span>}
              {isAdmin && <KbRestrictedBadge article={a} />}
            </div>
          </Link>
        </li>
      ))}
      {page && page.total > page.limit && (
        <li className="flex items-center justify-between gap-3 px-4 py-2 text-xs text-muted-foreground">
          <span>
            {page.skip + 1}-{Math.min(page.skip + page.limit, page.total)} of {page.total}
          </span>
          <span className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page.skip === 0} onClick={() => page.onSkip(Math.max(0, page.skip - page.limit))}>
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled={page.skip + page.limit >= page.total} onClick={() => page.onSkip(page.skip + page.limit)}>
              Next
            </Button>
          </span>
        </li>
      )}
    </ul>
  );
}
